import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { GridReadyEvent, GridOptions } from 'ag-grid-community';

import { DomLayoutType, RowModelType } from 'ag-grid-enterprise';
import { SharedModule } from '../../../shared/shared.module';

@Component({
    standalone: true,
    imports: [SharedModule],
  selector: 'app-ag-grid-default',
  templateUrl: './ag-grid-default.component.html',
  styleUrls: ['./ag-grid-default.component.scss']
})
export class AgGridDefaultComponent implements OnInit {
  // Input Properties
  @Input() gridOptions!: GridOptions;
  @Input() defaultColDef!: any;
  @Input() autoGroupColumnDef!: any;
  @Input() rowModelType!: RowModelType;
  @Input() pagination!: boolean;
  @Input() headerHeight!: number;
  @Input() paginationPageSize!: number;
  @Input() cacheBlockSize!: number;
  @Input() serverSideStoreType!: any | undefined;
  @Input() domLayout: DomLayoutType = 'autoHeight';
  @Input() columnDefs!: Array<any>;
  @Input() rowData!: Array<any>;
  @Input() isServerSide!: boolean;
  @Input() gridStyle!: any;
  @Input() rowHeight!: number;
  @Input() enableGrouping!: boolean;
  @Input() getDataPath!: (data: any) => string[];
  @Input() treeData!: boolean;

  // Custom pagination properties
  @Input() showCustomPagination: boolean = false;
  @Input() totalRecords: number = 0;
  @Input() currentPage: number = 0;
  @Input() totalPages: number = 1;
  @Input() pageSize: number = 10;
  @Input() isLoading: boolean = false;

  @Output() onGridReady: EventEmitter<any> = new EventEmitter<any>();
  @Output() onPageSizeChanged: EventEmitter<number> = new EventEmitter<number>();
  @Output() onPageChanged: EventEmitter<number> = new EventEmitter<number>();

  get gridClass() {
    if (document.body.classList.contains('darkmode')) {
      return 'ag-theme-alpine-dark custom-dark-theme';
    } else {
      return 'ag-theme-alpine custom-light-theme';
    }
  }

  constructor() { }

  ngOnInit() {
  }

  agGridReady(event: GridReadyEvent) {
    this.onGridReady.emit(event);
  }

  // Custom pagination methods
  hasGridData(): boolean {
    return this.rowData && this.rowData.length > 0;
  }

  getStartRecord(): number {
    if (this.totalRecords === 0) return 0;
    return (this.currentPage * this.pageSize) + 1;
  }

  getEndRecord(): number {
    if (this.totalRecords === 0) return 0;
    const endRecord = (this.currentPage + 1) * this.pageSize;
    return Math.min(endRecord, this.totalRecords);
  }

  onPageSizeChange(event: any): void {
    const newPageSize = parseInt(event.target.value, 10);
    this.onPageSizeChanged.emit(newPageSize);
  }

  goToFirstPage(): void {
    this.onPageChanged.emit(0);
  }

  goToPreviousPage(): void {
    if (this.currentPage > 0) {
      this.onPageChanged.emit(this.currentPage - 1);
    }
  }

  goToNextPage(): void {
    if (this.currentPage < this.calculatedTotalPages - 1) {
      this.onPageChanged.emit(this.currentPage + 1);
    }
  }

  goToLastPage(): void {
    this.onPageChanged.emit(this.calculatedTotalPages - 1);
  }

  onPageInputChange(event: any): void {
    const target = event.target as HTMLInputElement;
    const pageNumber = parseInt(target.value, 10);
    
    if (pageNumber && pageNumber >= 1 && pageNumber <= this.calculatedTotalPages) {
      this.onPageChanged.emit(pageNumber - 1); // Convert to 0-based index
    } else {
      // Reset to current page if invalid input
      target.value = (this.currentPage + 1).toString();
    }
  }

  get calculatedTotalPages(): number {
    return Math.ceil(this.totalRecords / this.pageSize);
  }
}
